import { useState, useEffect } from 'react';
import { apiService } from '../service/Api';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing user intelligence
 * Handles loading, creating, updating, and regenerating intelligence
 */
export const useUserIntelligence = () => {
  const { user } = useAuth();
  const [intelligence, setIntelligence] = useState(null);
  const [inputData, setInputData] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [exists, setExists] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const loadUserIntelligence = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getUserIntelligence();
      
      if (response.data.success) {
        setIntelligence(response.data.data);
        setExists(response.data.exists);
        setInputData(response.data.other || '');
      }
    } catch (err) {
      console.error('Error loading user intelligence:', err);
      setError('Failed to load intelligence. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createIntelligence = async (data) => {
    if (!data.trim()) {
      setError('Please enter some data (transcripts, emails, etc.) to generate your intelligence.');
      return { success: false };
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccessMessage('');

      const response = await apiService.createUserIntelligence({
        user_id: user.id,
        tenant_id: user.tenant_id,
        data
      });

      if (response.data.success) {
        setIntelligence(response.data.data);
        setExists(true);
        setSuccessMessage('Intelligence generated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        return { success: true, data: response.data.data };
      } else {
        setError(response.data.error || 'Failed to generate intelligence');
        return { success: false };
      }
    } catch (err) {
      console.error('Error generating intelligence:', err);
      setError(err.response?.data?.error || 'Failed to generate intelligence. Please try again.');
      return { success: false };
    } finally {
      setGenerating(false);
    }
  };

  const updateIntelligence = async (data) => {
    try {
      setUpdating(true);
      setError(null);
      setSuccessMessage('');

      const response = await apiService.updateUserIntelligence(data);

      if (response.data.success) {
        setSuccessMessage('Intelligence updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        return { success: true };
      } else {
        setError(response.data.error || 'Failed to update intelligence');
        return { success: false };
      }
    } catch (err) {
      console.error('Error updating intelligence:', err);
      setError(err.response?.data?.error || 'Failed to update intelligence. Please try again.');
      return { success: false };
    } finally {
      setUpdating(false);
    }
  };

  const regenerateIntelligence = async (data) => {
    if (!data.trim()) {
      setError('Please enter some data to regenerate your intelligence.');
      return { success: false };
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccessMessage('');

      const response = await apiService.createUserIntelligence({
        user_id: user.id,
        tenant_id: user.tenant_id,
        data
      });

      if (response.data.success) {
        setIntelligence(response.data.data);
        setSuccessMessage('Intelligence regenerated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        return { success: true, data: response.data.data };
      } else {
        setError(response.data.error || 'Failed to regenerate intelligence');
        return { success: false };
      }
    } catch (err) {
      console.error('Error regenerating intelligence:', err);
      setError(err.response?.data?.error || 'Failed to regenerate intelligence. Please try again.');
      return { success: false };
    } finally {
      setGenerating(false);
    }
  };

  return {
    intelligence,
    inputData,
    setInputData,
    loading,
    generating,
    updating,
    exists,
    error,
    successMessage,
    loadUserIntelligence,
    createIntelligence,
    updateIntelligence,
    regenerateIntelligence,
    setError,
    setSuccessMessage
  };
};

